import { useEffect, useRef, useState, useCallback } from "react";

// Continuous browser speech recognition (Castilian Spanish) with barge-in support.
export function useSpeechRecognition({ lang = "es-ES", onResult, onSpeechStart }) {
  const Rec =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!Rec;

  const recRef = useRef(null);
  const activeRef = useRef(false);
  const onResultRef = useRef(onResult);
  const onSpeechStartRef = useRef(onSpeechStart);
  const [listening, setListening] = useState(false);

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onSpeechStartRef.current = onSpeechStart; }, [onSpeechStart]);

  useEffect(() => {
    if (!supported) return undefined;
    const rec = new Rec();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    let announced = false;

    rec.onresult = (e) => {
      let finalText = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      if ((interim || finalText) && !announced) {
        announced = true;
        if (onSpeechStartRef.current) onSpeechStartRef.current();
      }
      if (finalText.trim()) {
        announced = false;
        if (onResultRef.current) onResultRef.current(finalText.trim());
      }
    };
    rec.onend = () => {
      setListening(false);
      if (activeRef.current) {
        try { rec.start(); setListening(true); } catch (_) { /* already started */ }
      }
    };
    rec.onerror = () => { /* network/no-speech errors auto-restart via onend */ };
    recRef.current = rec;

    return () => {
      activeRef.current = false;
      try { rec.stop(); } catch (_) { /* noop */ }
    };
  }, [supported, lang, Rec]);

  const start = useCallback(() => {
    if (!supported || activeRef.current) return;
    activeRef.current = true;
    try { recRef.current.start(); setListening(true); } catch (_) { /* noop */ }
  }, [supported]);

  const stop = useCallback(() => {
    activeRef.current = false;
    try { if (recRef.current) recRef.current.stop(); } catch (_) { /* noop */ }
    setListening(false);
  }, []);

  return { supported, listening, start, stop };
}
